import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Radar from 'radar-sdk-js';
import { RADAR_SERVICE_CONFIG, RadarServiceConfig } from './radar.config';

@Injectable({
  providedIn: 'root',
})
export class RadarService {
  private initialized = false;
  private config: RadarServiceConfig;

  constructor(
    @Optional() @Inject(RADAR_SERVICE_CONFIG) config: RadarServiceConfig | null,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (!config) {
      this.config = { publishableKey: '' };
    } else {
      this.config = config;
      if (isPlatformBrowser(platformId)) {
        this.initialize();
      }
    }
  }

  /**
   * Initializes Radar SDK with publishable key from config
   * @private
   */
  private initialize(): void {
    if (!this.initialized && this.config?.publishableKey) {
      Radar.initialize(this.config.publishableKey);
      this.initialized = true;
    }
  }

  /**
   * Returns the Radar SDK instance
   * Ensures SDK is initialized before returning
   * @returns Radar SDK instance
   */
  getRadar() {
    if (!this.initialized && isPlatformBrowser(this.platformId)) {
      this.initialize();
    }
    return Radar;
  }

  /**
   * Returns the country code(s) configured for Radar autocomplete
   * @returns Country code string (e.g., 'US' or 'US,CA') or undefined
   */
  getCountryCode(): string | undefined {
    return this.config?.countryCode;
  }

  /**
   * Returns the map style ID
   * @returns Map style ID or undefined
   */
  getMapStyle(): string | undefined {
    return this.config?.mapStyle;
  }

  /**
   * Returns the S3 config URL
   * @returns S3 config URL or undefined
   */
  getS3ConfigUrl(): string | undefined {
    return this.config?.s3ConfigUrl;
  }

  /**
   * Checks if Radar SDK has been initialized
   * @returns true if SDK is initialized, false otherwise
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
